import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const articles = [
  {
    title: "Why Essex is the Perfect Place for Wedding Photography and Film",
    content: `Essex has some of the most beautiful wedding venues in England. From Tudor manor houses to converted barns, modern country estates to intimate boutique spots. The variety is unreal.

We've been lucky enough to photograph and film at venues all over the county. Gaynes Park near Epping with its orangery glowing at golden hour. Leez Priory near Chelmsford looking like something out of a period drama. The courtyards at High House near Burnham on Crouch full of confetti and laughter. Every venue tells a different story.

What makes Essex so good for wedding coverage is the range. You can go from a grand Georgian ballroom at Gosfield Hall near Braintree to rolling parkland that stretches to the horizon. All in one day. That kind of variety means your photos and film will have real depth and texture, not the same backdrop on repeat.

Couples come from all over. Chelmsford, Colchester, Brentwood, Southend, Basildon, Harlow. And the venues are spread right across the county so wherever you are in Essex, there's something incredible nearby. Plus it's close enough to London that you get the countryside feel without the three hour drive. That's why so many couples are choosing Essex venues right now. And why we're kept very busy.`,
  },
  {
    title: "How to Choose the Right Wedding Photographer and Videographer in Essex",
    content: `This is one of the biggest decisions you'll make when planning your wedding. Your flowers will die. Your cake will be eaten. But your photos and film? You'll have those forever. So it's worth getting right.

First, watch their work properly. Not just a highlights reel on Instagram. Watch full films. Look through full galleries. Does it make you feel something? Do the photos look natural, not posed? Do the films tell a story or just show a sequence of events? The best wedding photographers and videographers are storytellers. Not just people with cameras.

Second, check if they've worked at your venue before. A team that knows Braxted Park near Witham, Baddow Park in Chelmsford, or Gosfield Hall near Braintree will already know the best spots, the tricky lighting, and the flow of the day. That experience matters more than you'd think.

Third, think about how they work together. Do the photographer and videographer coordinate? Or do they get in each other's way? We send a proper team to every wedding. Everyone knows their role and works together. Whether it's a big celebration in Colchester or an intimate gathering near Southend, you'll never feel crowded or overwhelmed.

Finally, read the reviews. Not just the star ratings. Read what real couples say about how they felt. The best wedding coverage makes you cry in the best possible way.`,
  },
  {
    title: "What Makes Cinematic Wedding Coverage Different?",
    content: `There's a massive difference between someone recording your wedding and someone creating a cinematic wedding film and photo collection. Understanding that difference will save you a lot of regret.

Traditional wedding video means pointing a camera and pressing record. You get a long, chronological edit that most people watch once and never return to. Cinematic filmmaking is a completely different craft. We use cinema cameras, prime lenses, stabilisers, and drones to create footage that has real depth, real movement, real emotion.

The edit is where it all comes together. We don't just plop your day in order. We weave your vows over golden hour footage. We cut between the speeches and the quiet morning moments. We use music to build emotion so that even people who weren't at your wedding will feel something when they watch it.

Audio matters too. We capture your vows, your speeches, the ambient sounds of your day with proper equipment. Every whispered promise, every burst of laughter, crystal clear.

The same goes for photography. We don't do awkward poses or forced smiles. We capture real moments with an editorial eye. The goal is photos that feel like they belong in a magazine but also make your mum cry.

That's the difference. Recording what happened versus making you feel what it was like to be there.`,
  },
  {
    title: "Why You Should Book Your Wedding Photographer and Videographer as One Team",
    content: `A lot of couples book their photographer and videographer separately. It makes sense on the surface. You find someone whose photos you love, then you find someone whose films you love. But on the day itself, it can cause real problems.

Two separate suppliers who have never worked together will step on each other's toes. The videographer needs the couple walking towards them. The photographer needs them turning the other way. One is trying to get a wide shot while the other is in the frame. It gets messy and it shows in the final results.

When you book a team that does both photo and video together, none of that happens. Our photographers and videographers work side by side every weekend. They know exactly where each other will be. They share angles, share moments, and cover twice the ground without ever getting in each other's way.

It also means one point of contact. One timeline. One style. Your photos and your film will feel like they belong together because the same team created both. That consistency is something you can't fake by hiring two separate people and hoping for the best.

Plus it's usually better value. All inclusive packages mean no surprises, no awkward negotiations, and no wondering if you've forgotten to book something.`,
  },
  {
    title: "The Best Time of Year for Wedding Photos and Film in Essex",
    content: `Every season in Essex has something to offer. But the light, the colours, and the feel of your photos and film will change a lot depending on when you get married.

Spring weddings are fresh. Blossom in the gardens at Gaynes Park near Epping, new green across the parkland at Braxted Park near Witham, soft light that flatters everyone. April and May are particularly gorgeous for outdoor couple portraits. The golden hour is generous but not too late in the evening.

Summer is peak season for a reason. Long days mean more time for photos and filming. The light lasts until 9pm in June and July. That gives us loads of flexibility with the schedule. Outdoor ceremonies look incredible on film. The downside is heat. We always tell couples to plan portraits before the sun gets too harsh. Our Chelmsford, Brentwood and Colchester couples love summer for the garden shots.

Autumn is underrated. The colours are unreal. Burnt oranges, deep reds, golden leaves scattered across the ground. Venues like Leez Priory near Great Leighs and Gosfield Hall near Braintree look like something from a painting. The light is warm and low, which is perfect for both photos and film.

Winter weddings have their own magic. Fairy lights, candles, frost on the lawns. The contrast between warm interiors and dramatic winter skies gives your coverage real atmosphere. Just make sure your timeline accounts for the early sunset. Golden hour in December is around 3pm. Venues near Southend like Apton Hall and The Lawn look stunning in winter light.`,
  },
  {
    title: "Wedding Day Timeline Tips from an Essex Photo and Video Team",
    content: `After covering hundreds of weddings across Essex, we've learned what makes a day run smoothly and what causes stress for everyone involved. Here are the timeline tips we share with every couple we work with.

Give yourself more time for getting ready than you think you need. Seriously. Hair and makeup always run over. If we arrive and the bride is still in rollers with 20 minutes to go, it changes the energy of the whole morning. Build in a buffer. Your photos and film from the morning prep will be so much better when everyone is relaxed.

Schedule couple portraits during golden hour, not straight after the ceremony. Right after the ceremony you're buzzing, guests want to hug you, and the light is usually overhead and harsh. Golden hour gives you soft, warm light that makes everything look incredible on camera. We'll work with your coordinator to find the perfect 20 minute window.

Don't skip the details. Rings, shoes, invitations, flowers, perfume bottles. These take about 10 minutes to photograph and film properly and they add so much to the final story. Leave them out on a table for us and we'll do the rest.

Give speeches some breathing room. Back to back speeches with no breaks make it harder for us to change angles and batteries. A short pause between each one is all we need. Your guests will appreciate the break too.

Trust your suppliers. We've done this hundreds of times. If we suggest a quick walk to a spot you hadn't considered, it's because we know the light there is about to be perfect. Five minutes of trust can give you photos and film you'll treasure forever.`,
  },
  {
    title: "Drone Footage at Essex Weddings. What You Need to Know",
    content: `Drone footage can completely transform your wedding film. That sweeping aerial shot of your venue, your guests on the lawn, the two of you walking hand in hand through the grounds. It adds a cinematic scale that ground cameras simply can't replicate.

We use drones at most of our Essex weddings and the results are always worth it. Venues like Gaynes Park near Epping with 500 acres of parkland or Braxted Park near Witham with its grand estate look absolutely breathtaking from above. Even smaller venues like Houchins near Coggeshall or Mulberry House near Ongar benefit from a few aerial shots that show the setting and the scale of the day.

There are a few things to know though. Drones can't fly everywhere. We need to check airspace restrictions for your venue, and weather plays a big role. High winds or heavy rain mean the drone stays in the bag. Safety always comes first. Some venues near Southend and the coast can be windier so we always check conditions on the day.

We also keep drone flying to specific moments so it doesn't dominate the day. Usually we'll fly during the drinks reception when guests are outside, or during couple portraits. It takes about 10 minutes and nobody even notices. The footage gets woven into your film so it feels natural, not like a property listing.

All our drone pilots are fully licensed and insured. We handle the paperwork and permissions so you don't have to think about it. Whether your venue is in Chelmsford, Colchester, Brentwood, or anywhere else in Essex, we've got it covered.`,
  },
  {
    title: "What Happens After Your Wedding Day? The Editing Process Explained",
    content: `Your wedding day flies by. But for us, the real craft begins once the cameras are packed away. The editing process is where your photos and film come to life.

For photography, we go through every single image from the day. A typical Essex wedding gives us between 3,000 and 5,000 raw photos. We cull that down to the best 500 to 800 images and then edit each one individually. Colour grading, exposure, skin tones, cropping. Every photo gets proper attention. No batch filters. No one size fits all presets.

For film, the process is even more involved. We typically capture 6 to 10 hours of footage across multiple cameras. That all needs to be synced, reviewed, and logged before we even start the edit. Then comes the storytelling. Choosing the music, building the narrative, weaving together the ceremony audio with the best visual moments of the day.

We deliver multiple films. A highlight reel that captures the essence of your day in a few minutes. A longer documentary edit that includes more of the speeches, the ceremony, and the quieter moments. Plus individual ceremony and speech edits so you can watch those back in full whenever you want.

The whole process takes around 8 to 12 weeks. We know that feels like a long time when you're excited to see everything. But rushing the edit means cutting corners, and we'd rather take the time to get it right. When you finally press play, you'll understand why.`,
  },
];

function BlogArticle({ article, index }) {
  const [expanded, setExpanded] = useState(false);
  const paragraphs = article.content.split('\n\n');

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.1 }}
      data-testid={`blog-article-${index}`}
      className="border-l-2 border-[#C2A878]/20 pl-6 md:pl-10"
      itemScope
      itemType="https://schema.org/Article"
    >
      <h3 className="font-serif text-xl sm:text-2xl text-[#F5F5F0] tracking-tight leading-snug" itemProp="headline">
        {article.title}
      </h3>
      <div className="mt-4 space-y-4" itemProp="articleBody">
        <p className="text-sm md:text-base text-[#A19D94] font-sans leading-relaxed">
          {paragraphs[0]}
        </p>
        {paragraphs.slice(1).map((paragraph, j) => (
          <p
            key={j}
            className={`text-sm md:text-base text-[#A19D94] font-sans leading-relaxed ${!expanded ? "sr-only" : ""}`}
            aria-hidden={!expanded}
          >
            {paragraph}
          </p>
        ))}
      </div>
      {paragraphs.length > 1 && (
        <button
          onClick={() => setExpanded(!expanded)}
          data-testid={`blog-toggle-${index}`}
          aria-expanded={expanded}
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#C2A878] hover:text-[#d4bc92] transition-colors font-sans"
        >
          {expanded ? "Read less" : "Read more"}
          <ChevronDown
            size={14}
            className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}
    </motion.article>
  );
}

export default function BlogContent() {
  return (
    <section
      data-testid="blog-content-section"
      className="bg-[#11100F] py-24 md:py-32"
      aria-label="Wedding photography and videography guides"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-16"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Essex Wedding Photo & Video Insights
          </p>
          <h2
            data-testid="blog-heading"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#F5F5F0]"
          >
            Everything you need to know about Essex wedding photography and videography
          </h2>
        </motion.div>

        <div className="space-y-16">
          {articles.map((article, i) => (
            <BlogArticle key={article.title} article={article} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
